import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { 
  Users, 
  Package, 
  Lock, 
  Unlock, 
  Loader2,
  Search,
  Building2,
  Sparkles,
  Check,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function for German module names
const getModuleName = (moduleId) => {
  const moduleNames = {
    'backshop': 'Backshop',
    'lotto': 'Lotto',
    'mhd_control': 'MHD-Verwaltung',
    'temperature_control': 'Temperaturkontrolle',
    'tasks': 'Aufgaben',
    'unterweisungen': 'Unterweisungen',
    'employee_management': 'Mitarbeiterverwaltung',
    'general_writeoffs': 'Allgemeine Abschriften',
    'instructions': 'Schulungen',
    'orders': 'Bestellungen'
  };
  return moduleNames[moduleId] || moduleId;
};

export const CustomerModuleManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/superadmin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerSubscription = async (customerId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/superadmin/customers/${customerId}/subscription`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription(response.data);
      setSelectedCustomer(response.data.customer);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Fehler beim Laden des Abonnements');
    } finally {
      setLoading(false);
    }
  };

  const handleLockModule = async (moduleId, shouldLock) => {
    try {
      setActionLoading(moduleId);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/api/superadmin/customers/${selectedCustomer.id}/lock-module`,
        {
          module_id: moduleId,
          locked: shouldLock,
          reason: shouldLock ? 'Durch Superadmin gesperrt' : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(shouldLock ? 'Modul gesperrt' : 'Modul freigegeben');
      
      // Refresh subscription data
      fetchCustomerSubscription(selectedCustomer.id);
    } catch (error) {
      console.error('Error locking/unlocking module:', error);
      toast.error('Fehler beim Sperren/Freigeben des Moduls');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnlockPremiumModule = async (moduleId, shouldUnlock) => {
    try {
      setActionLoading(moduleId);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/api/superadmin/customers/${selectedCustomer.id}/unlock-premium-module`,
        {
          module_id: moduleId,
          unlock: shouldUnlock
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(shouldUnlock ? 'Premium-Modul freigeschaltet' : 'Premium-Modul entfernt');
      
      // Refresh subscription data
      fetchCustomerSubscription(selectedCustomer.id);
    } catch (error) {
      console.error('Error with premium module:', error);
      toast.error('Fehler beim Premium-Modul');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.customer_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isModuleLocked = (moduleId) => {
    return subscription?.subscription?.locked_modules?.includes(moduleId) || false;
  };

  const isModuleUnlockedPremium = (moduleId) => {
    return subscription?.subscription?.unlocked_premium_modules?.includes(moduleId) || false;
  };

  const getModuleType = (moduleId) => {
    // Check if it's included in package
    if (subscription?.subscription?.included_modules?.includes(moduleId)) {
      return 'standard';
    }
    return 'premium';
  };

  // Get all standard modules from subscription
  const standardModules = subscription?.subscription?.included_modules || [];
  
  // Define available premium modules (würde normalerweise aus DB kommen)
  const premiumModules = ['schichtplanung', 'erweiterte_statistiken', 'automatische_bestellungen'];

  if (loading && !subscription) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Kunden Modul-Verwaltung</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Module für Kunden sperren oder Premium-Module freischalten
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Customer List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                Kunden ({filteredCustomers.length})
              </CardTitle>
              <div className="mt-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Kunde suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[400px] lg:max-h-[600px] overflow-y-auto">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    onClick={() => fetchCustomerSubscription(customer.id)}
                    className={`p-3 sm:p-4 border-b cursor-pointer transition-colors hover:bg-accent ${
                      selectedCustomer?.id === customer.id ? 'bg-accent' : ''
                    }`}
                  >
                    <div className="font-medium text-sm sm:text-base">{customer.company_name}</div>
                    <div className="text-xs sm:text-sm text-muted-foreground">{customer.customer_number}</div>
                    {customer.industry && (
                      <Badge variant="outline" className="mt-1 sm:mt-2 text-xs">
                        {customer.industry}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Module Management */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {!selectedCustomer ? (
              <Card>
                <CardContent className="py-8 sm:py-12 text-center px-4">
                  <Package className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-muted-foreground/50 mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Kein Kunde ausgewählt</h3>
                  <p className="text-sm text-muted-foreground">
                    Wählen Sie einen Kunden aus der Liste, um Module zu verwalten.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Customer Info */}
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold">{selectedCustomer.company_name}</h2>
                        <p className="text-xs sm:text-sm text-muted-foreground">{selectedCustomer.customer_number}</p>
                      </div>
                      <div className="sm:text-right">
                        <div className="text-xs sm:text-sm text-muted-foreground mb-1">Aktuelles Paket</div>
                        <Badge className="bg-primary">
                          {subscription?.package?.name || 'Kein Paket'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Standard Modules */}
                <Card>
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <Package className="h-4 w-4 sm:h-5 sm:w-5" />
                      Basis-Module ({standardModules.length})
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      Im Paket enthaltene Module - können gesperrt werden
                    </p>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-0">
                    <div className="space-y-2 sm:space-y-3">
                      {standardModules.map((moduleId) => {
                        const locked = isModuleLocked(moduleId);
                        return (
                          <div
                            key={moduleId}
                            className="flex items-center justify-between p-2 sm:p-3 border rounded-lg gap-2"
                          >
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className={`p-1.5 sm:p-2 rounded flex-shrink-0 ${locked ? 'bg-red-100 dark:bg-red-900/20' : 'bg-green-100 dark:bg-green-900/20'}`}>
                                {locked ? (
                                  <Lock className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 dark:text-red-400" />
                                ) : (
                                  <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 dark:text-green-400" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm sm:text-base truncate">{getModuleName(moduleId)}</div>
                                <div className="text-xs text-muted-foreground truncate">{moduleId}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {locked && (
                                <Badge variant="destructive" className="text-xs hidden sm:inline-flex">Gesperrt</Badge>
                              )}
                              <Switch
                                checked={!locked}
                                disabled={actionLoading === moduleId}
                                onCheckedChange={(checked) => handleLockModule(moduleId, !checked)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Premium Modules */}
                <Card className="border-purple-200 dark:border-purple-800">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-purple-700 dark:text-purple-400">
                      <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                      Premium-Module ({premiumModules.length})
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      Kostenpflichtige Module - können manuell freigeschaltet werden
                    </p>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-0">
                    <div className="space-y-2 sm:space-y-3">
                      {premiumModules.map((moduleId) => {
                        const unlocked = isModuleUnlockedPremium(moduleId);
                        return (
                          <div
                            key={moduleId}
                            className="flex items-center justify-between p-2 sm:p-3 border border-purple-200 dark:border-purple-800 rounded-lg bg-purple-50/50 dark:bg-purple-950/20 gap-2"
                          >
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className={`p-1.5 sm:p-2 rounded flex-shrink-0 ${unlocked ? 'bg-purple-100 dark:bg-purple-900/20' : 'bg-gray-100 dark:bg-gray-800'}`}>
                                {unlocked ? (
                                  <Unlock className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 dark:text-purple-400" />
                                ) : (
                                  <X className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm sm:text-base truncate">{getModuleName(moduleId)}</div>
                                <div className="text-xs text-muted-foreground truncate">{moduleId}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {unlocked && (
                                <Badge className="bg-purple-600 hover:bg-purple-700 text-xs hidden sm:inline-flex">
                                  Freigeschaltet
                                </Badge>
                              )}
                              <Switch
                                checked={unlocked}
                                disabled={actionLoading === moduleId}
                                onCheckedChange={(checked) => handleUnlockPremiumModule(moduleId, checked)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomerModuleManagement;
